// src/components/UniversalConverter.js

import React, { useState, useEffect, useRef } from "react";
import {
  FaUpload,
  FaCopy,
  FaFileExport,
  FaArrowUp,
  FaArrowDown,
  FaMoon,
  FaSun,
} from "react-icons/fa";
import xmlFormatter from "xml-formatter";

/**
 * This single component handles:
 *   1) JSON → CSV
 *   2) CSV → JSON
 *   3) JSON → XML
 * 
 * with line numbers, advanced toggles, dark mode, 
 * top/bottom scrolling, copying, exporting, preview, 
 * and validation in one place.
 */
const UniversalConverter = () => {
  // ------------------ States ------------------
  const [mode, setMode] = useState("jsonToCsv"); // "jsonToCsv", "csvToJson", or "jsonToXml"
  const [inputText, setInputText] = useState("");
  const [outputText, setOutputText] = useState("");
  const [error, setError] = useState("");
  const [copySuccess, setCopySuccess] = useState(false);
  const [processing, setProcessing] = useState(false);

  // Line numbers toggle
  const [showLineNumbers, setShowLineNumbers] = useState(true);

  // Dark mode
  const [darkMode, setDarkMode] = useState(false);

  // Advanced toggles (depending on mode)
  // For JSON→CSV:
  const [delimiter, setDelimiter] = useState(",");
  const [ignoreNullFields, setIgnoreNullFields] = useState(false);

  // For CSV→JSON:
  const [trimQuotes, setTrimQuotes] = useState(false);
  const [ignoreEmptyLines, setIgnoreEmptyLines] = useState(false);

  // For JSON→XML:
  const [rootElement, setRootElement] = useState("root");
  const [flattenArrays, setFlattenArrays] = useState(false);
  const [ignoreNullXml, setIgnoreNullXml] = useState(false);

  // "Show Advanced" panel
  const [showAdvanced, setShowAdvanced] = useState(false);

  // Example data for each mode
  const exampleData = {
    jsonToCsv: `{
    "company": "SFDC247",
    "employees": [
      {
        "id": 1,
        "name": "John Doe",
        "role": "Salesforce Developer",
        "projects": ["Project Alpha", "Project Beta"]
      },
      {
        "id": 2,
        "name": "Jane Smith",
        "role": "Salesforce Architect",
        "projects": ["Project Gamma"]
      }
    ]
  }`,
    csvToJson: `company,employees.id,employees.name,employees.role,employees.projects
  SFDC247,1,John Doe,Salesforce Developer,"Project Alpha & Project Beta"
  SFDC247,2,Jane Smith,Salesforce Architect,"Project Gamma"`,
    jsonToXml: `{
    "company": "SFDC247",
    "employees": [
      {
        "id": 1,
        "name": "John Doe",
        "role": "Salesforce Developer",
        "projects": ["Project Alpha", "Project Beta"]
      },
      {
        "id": 2,
        "name": "Jane Smith",
        "role": "Salesforce Architect",
        "projects": ["Project Gamma"]
      }
    ]
  }`,
  };

  // Refs for input/output areas
  const inputAreaRef = useRef(null);
  const lineNumbersRef = useRef(null);
  const outputAreaRef = useRef(null);

  // Dark Mode
  useEffect(() => {
    if (darkMode) {
      document.body.style.backgroundColor = "#2d2d2d";
      document.body.style.color = "#f0f0f0";
    } else {
      document.body.style.backgroundColor = "";
      document.body.style.color = "";
    }
  }, [darkMode]);

  // ------------- Conversion Helpers -------------
  // JSON -> CSV
  const flattenObject = (obj, prefix = "") => {
    return Object.keys(obj).reduce((acc, key) => {
      const fullKey = prefix ? `${prefix}.${key}` : key;
      let val = obj[key];

      if (val === null && ignoreNullFields) {
        // skip null
        return acc;
      }
      if (typeof val === "object" && val !== null && !Array.isArray(val)) {
        Object.assign(acc, flattenObject(val, fullKey));
      } else {
        // if array, store as JSON string
        if (Array.isArray(val)) {
          val = JSON.stringify(val);
        }
        acc[fullKey] = val;
      }
      return acc;
    }, {});
  };
  const jsonToCsv = (jsonData) => {
    if (!Array.isArray(jsonData)) {
      if (jsonData && typeof jsonData === "object") {
        jsonData = [jsonData];
      } else {
        throw new Error("Input JSON must be an array or object for JSON→CSV.");
      }
    }
    const flattened = jsonData.map((row) => flattenObject(row));
    // gather keys
    const allKeys = Array.from(
      flattened.reduce((acc, row) => {
        Object.keys(row).forEach((k) => acc.add(k));
        return acc;
      }, new Set())
    );
    // build CSV
    const csvRows = [];
    csvRows.push(allKeys.join(delimiter)); // header
    for (const row of flattened) {
      csvRows.push(allKeys.map((k) => (row[k] !== undefined ? row[k] : "")).join(delimiter));
    }
    return csvRows.join("\n");
  };

  // CSV -> JSON
  const csvToJson = (csv) => {
    let rows = csv.trim().split("\n");
    if (ignoreEmptyLines) {
      rows = rows.filter((r) => r.trim() !== "");
    }
    if (rows.length === 0) {
      return "[]";
    }
    const headers = rows[0].split(",").map((h) => h.trim());
    const data = rows.slice(1).map((line) => {
      let cols = line.split(",");
      if (trimQuotes) {
        cols = cols.map((c) => c.replace(/^"(.*)"$/, "$1").trim());
      } else {
        cols = cols.map((c) => c.trim());
      }
      const obj = {};
      headers.forEach((header, idx) => {
        obj[header] = cols[idx] || "";
      });
      return obj;
    });
    return JSON.stringify(data, null, 2);
  };

  // JSON -> XML
  const jsonToXmlHelper = (value, tagName) => {
    if (value === null && ignoreNullXml) {
      return "";
    }
    if (
      typeof value === "string" ||
      typeof value === "number" ||
      typeof value === "boolean"
    ) {
      return `<${tagName}>${value}</${tagName}>`;
    }
    if (Array.isArray(value)) {
      if (flattenArrays) {
        const singular = tagName.endsWith("s") ? tagName.slice(0, -1) : "item";
        return `<${tagName}>` +
          value
            .map((item) => `<${singular}>${jsonToXmlHelper(item, singular)}</${singular}>`)
            .join("") +
          `</${tagName}>`;
      } else {
        return value.map((item) => jsonToXmlHelper(item, tagName)).join("");
      }
    }
    if (typeof value === "object" && value !== null) {
      return Object.keys(value)
        .map((key) => jsonToXmlHelper(value[key], key))
        .join("");
    }
    return `<${tagName}/>`;
  };
  const jsonToXml = (parsedJson) => {
    const rawXml = `<${rootElement}>${jsonToXmlHelper(parsedJson, rootElement)}</${rootElement}>`;
    // We can use xml-formatter
    return xmlFormatter(rawXml, {
      indentation: "  ",
      collapseContent: true,
    });
  };

  // ------------- Handle Convert -------------
  const handleConvert = () => {
    if (!inputText.trim()) {
      setError("No input provided.");
      setOutputText("");
      return;
    }
    setProcessing(true);

    setTimeout(() => {
      try {
        let result = "";
        if (mode === "jsonToCsv") {
          // parse as JSON -> CSV
          const parsed = JSON.parse(inputText);
          result = jsonToCsv(parsed);
        } else if (mode === "csvToJson") {
          // parse as CSV -> JSON
          result = csvToJson(inputText);
        } else if (mode === "jsonToXml") {
          // parse as JSON -> XML
          const parsed = JSON.parse(inputText);
          result = jsonToXml(parsed);
        }
        setOutputText(result);
        setError("");
        setCopySuccess(false);
      } catch (err) {
        setOutputText("");
        setError(`Error: ${err.message}`);
        setCopySuccess(false);
      }
      setProcessing(false);
    }, 500);
  };

  // ------------- Basic Actions -------------
  const handleClear = () => {
    setInputText("");
    setOutputText("");
    setError("");
    setCopySuccess(false);
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(outputText);
    setCopySuccess(true);
    setTimeout(() => setCopySuccess(false), 2000);
  };

  // Example Data
  const handleUseExample = () => {
    setInputText(exampleData[mode]);
    setOutputText("");
    setError("");
    setCopySuccess(false);
  };

  // Export
  const handleExport = () => {
    let fileType = "text/plain";
    let fileExt = ".txt";
    if (mode === "jsonToCsv") {
      fileType = "text/csv";
      fileExt = ".csv";
    } else if (mode === "csvToJson") {
      fileType = "application/json";
      fileExt = ".json";
    } else if (mode === "jsonToXml") {
      fileType = "application/xml";
      fileExt = ".xml";
    }
    const blob = new Blob([outputText], { type: fileType });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = "output" + fileExt;
    link.click();
  };

  // Validate
  const handleValidate = () => {
    if (!outputText.trim()) {
      alert("No output to validate!");
      return;
    }
    if (mode === "jsonToCsv") {
      // basic CSV validation
      const rows = outputText.split("\n");
      if (rows.length < 2) {
        alert("CSV seems empty or has no data rows!");
        return;
      }
      const headerCols = rows[0].split(delimiter).length;
      for (let i = 1; i < rows.length; i++) {
        const cols = rows[i].split(delimiter).length;
        if (cols !== headerCols) {
          alert(
            `Mismatch at row ${i + 1}. Expected ${headerCols} columns, got ${cols}.`
          );
          return;
        }
      }
      alert("CSV is valid!");
    } else if (mode === "csvToJson") {
      // try parse as JSON
      try {
        JSON.parse(outputText);
        alert("JSON is valid!");
      } catch (err) {
        alert("Invalid JSON: " + err.message);
      }
    } else if (mode === "jsonToXml") {
      // minimal XML check
      try {
        const parser = new DOMParser();
        const doc = parser.parseFromString(outputText, "application/xml");
        const errorNode = doc.querySelector("parsererror");
        if (errorNode) {
          alert("Invalid XML:\n" + errorNode.textContent);
        } else {
          alert("XML is well-formed!");
        }
      } catch (err) {
        alert("XML parse error: " + err.message);
      }
    }
  };

  // Preview in new tab
  const handlePreview = () => {
    if (!outputText.trim()) {
      alert("No output to preview!");
      return;
    }
    // For CSV or JSON, we do <pre>; for XML we do the same
    const newWin = window.open("", "_blank", "width=600,height=600");
    newWin.document.write(
      `<pre style="white-space:pre; font-family:monospace;">${outputText
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")}</pre>`
    );
    newWin.document.close();
  };

  // Scroll top/bottom
  const scrollOutputTop = () => {
    if (outputAreaRef.current) {
      outputAreaRef.current.scrollTop = 0;
    }
  };
  const scrollOutputBottom = () => {
    if (outputAreaRef.current) {
      outputAreaRef.current.scrollTop = outputAreaRef.current.scrollHeight;
    }
  };

  // Render input text area (300px) with optional line numbers
  const renderInputArea = () => {
    const lines = inputText.split("\n");
    return (
      <div style={{ position: "relative", display: "flex", height: "300px" }}>
        {showLineNumbers && (
          <div
            style={{
              overflowY: "auto",
              backgroundColor: "#f8f9fa",
              borderRight: "1px solid #ccc",
              width: "50px",
              textAlign: "right",
              userSelect: "none",
              color: "#6c757d",
              paddingTop: "5px",
            }}
          >
            {lines.map((_, i) => (
              <div key={i} style={{ height: "1.5em", lineHeight: "1.5em" }}>
                {i + 1}
              </div>
            ))}
          </div>
        )}
        <textarea
          ref={inputAreaRef}
          value={inputText}
          onChange={(e) => {
            setInputText(e.target.value);
            setError("");
          }}
          placeholder={`Enter ${
            mode === "csvToJson" ? "CSV" : "JSON"
          } data here...`}
          style={{
            flex: 1,
            resize: "none",
            borderRadius: 0,
            border: "2px solid #ccc",
            overflowY: "auto",
            fontFamily: "monospace",
            paddingLeft: showLineNumbers ? "10px" : "1em",
          }}
        />
      </div>
    );
  };

  // Render output text area (300px)
  const renderOutputArea = () => {
    return (
      <div
        id="universal-output-area"
        ref={outputAreaRef}
        style={{
          height: "300px",
          overflowY: "auto",
          backgroundColor: "#f8f9fa",
          border: "2px solid #ccc",
          borderRadius: "5px",
        }}
      >
        <pre
          style={{
            margin: 0,
            padding: "10px",
            whiteSpace: "pre",
            fontFamily: "monospace",
          }}
        >
          {outputText}
        </pre>
      </div>
    );
  };

  // -------------- Conditionals for advanced toggles --------------
  const renderAdvancedToggles = () => {
    if (mode === "jsonToCsv") {
      // Show delimiter, ignoreNullFields
      return (
        <>
          <label className="me-3">
            Delimiter:
            <select
              style={{ marginLeft: "5px" }}
              value={delimiter}
              onChange={(e) => setDelimiter(e.target.value)}
            >
              <option value=",">Comma (,)</option>
              <option value=";">Semicolon (;)</option>
              <option value="\t">Tab</option>
            </select>
          </label>
          <label className="me-3">
            <input
              type="checkbox"
              checked={ignoreNullFields}
              onChange={(e) => setIgnoreNullFields(e.target.checked)}
            />{" "}
            Ignore Null Fields
          </label>
        </>
      );
    } else if (mode === "csvToJson") {
      // Show trimQuotes, ignoreEmptyLines
      return (
        <>
          <label className="me-3">
            <input
              type="checkbox"
              checked={trimQuotes}
              onChange={(e) => setTrimQuotes(e.target.checked)}
            />{" "}
            Trim Quotes
          </label>
          <label className="me-3">
            <input
              type="checkbox"
              checked={ignoreEmptyLines}
              onChange={(e) => setIgnoreEmptyLines(e.target.checked)}
            />{" "}
            Ignore Empty Lines
          </label>
        </>
      );
    } else if (mode === "jsonToXml") {
      // Show rootElement, flattenArrays, ignoreNullXml
      return (
        <>
          <label className="me-3">
            Root Element:
            <input
              type="text"
              value={rootElement}
              onChange={(e) => setRootElement(e.target.value)}
              style={{ marginLeft: "5px" }}
            />
          </label>
          <label className="me-3">
            <input
              type="checkbox"
              checked={flattenArrays}
              onChange={(e) => setFlattenArrays(e.target.checked)}
            />{" "}
            Flatten Arrays
          </label>
          <label className="me-3">
            <input
              type="checkbox"
              checked={ignoreNullXml}
              onChange={(e) => setIgnoreNullXml(e.target.checked)}
            />{" "}
            Ignore Null Fields
          </label>
        </>
      );
    }
    return null;
  };

  return (
    <div className="container mt-4" style={{ maxWidth: "900px" }}>
      {/* Dark Mode Toggle */}
      <div style={{ textAlign: "right", marginBottom: "10px" }}>
        <button
          className="btn btn-sm btn-secondary"
          onClick={() => setDarkMode(!darkMode)}
        >
          {darkMode ? <FaSun /> : <FaMoon />} {darkMode ? "Light" : "Dark"} Mode
        </button>
      </div>

      {/* Conversion Type Switcher */}
      <div className="text-center mb-3">
        <select
          className="form-select"
          style={{ maxWidth: "200px", margin: "auto" }}
          value={mode}
          onChange={(e) => {
            setMode(e.target.value);
            setOutputText("");
            setError("");
            setCopySuccess(false);
          }}
        >
          <option value="jsonToCsv">JSON → CSV</option>
          <option value="csvToJson">CSV → JSON</option>
          <option value="jsonToXml">JSON → XML</option>
        </select>
      </div>

      {/* Possibly error/spinner */}
      <div className="text-center mb-3">
        {processing && <div className="alert alert-info">Processing...</div>}
        {error && <div className="alert alert-danger">{error}</div>}
      </div>

      {/* Buttons row: Example, etc. */}
      <div className="text-center mb-3">
        <button className="btn btn-info me-2" onClick={handleUseExample}>
          Use Example Data
        </button>
        <button
          className="btn btn-success me-2"
          onClick={handleConvert}
          disabled={processing}
        >
          {processing ? "Converting..." : "Convert"}
        </button>
        <button className="btn btn-secondary" onClick={handleClear}>
          Clear
        </button>
      </div>

      {/* Show line numbers toggle, Show advanced */}
      <div className="text-center mb-3">
        <label className="me-3">
          <input
            type="checkbox"
            checked={showLineNumbers}
            onChange={(e) => setShowLineNumbers(e.target.checked)}
          />{" "}
          Show Line Numbers
        </label>
        <button
          className="btn btn-outline-primary"
          onClick={() => setShowAdvanced(!showAdvanced)}
        >
          {showAdvanced ? "Hide Advanced" : "Show Advanced"}
        </button>
      </div>

      {/* If advanced is open, show relevant toggles */}
      {showAdvanced && (
        <div
          style={{
            border: "1px solid #ccc",
            borderRadius: "5px",
            padding: "10px",
            marginBottom: "10px",
            backgroundColor: darkMode ? "#444" : "#f8f9fa",
          }}
        >
          {renderAdvancedToggles()}
        </div>
      )}

      {/* Input area (300px) */}
      <div className="row mb-3">
        <div className="col-md-12">{renderInputArea()}</div>
      </div>

      {/* Output area (300px) */}
      {outputText && (
        <div className="row">
          <div className="col-md-12">
            <h5>Converted Output</h5>
            <div
              id="universal-output-area"
              ref={outputAreaRef}
              style={{
                height: "300px",
                overflowY: "auto",
                backgroundColor: "#f8f9fa",
                border: "2px solid #ccc",
                borderRadius: "5px",
              }}
            >
              <pre
                style={{
                  margin: 0,
                  padding: "10px",
                  whiteSpace: "pre",
                  fontFamily: "monospace",
                }}
              >
                {outputText}
              </pre>
            </div>

            {/* Buttons at bottom, two rows: first row scroll, second row copy/export/preview/validate */}
            {/* Row 1: Scroll to top/bottom */}
            <div
              style={{
                display: "flex",
                gap: "8px",
                justifyContent: "center",
                marginTop: "8px",
              }}
            >
              <button className="btn btn-secondary" onClick={scrollOutputTop}>
                <FaArrowUp /> Scroll to Top
              </button>
              <button className="btn btn-secondary" onClick={scrollOutputBottom}>
                <FaArrowDown /> Scroll to Bottom
              </button>
            </div>

            {/* Row 2: copy, export, preview, validate */}
            <div
              style={{
                display: "flex",
                gap: "8px",
                justifyContent: "center",
                marginTop: "8px",
              }}
            >
              <button
                className={`btn ${copySuccess ? "btn-success" : "btn-primary"}`}
                onClick={handleCopy}
              >
                <FaCopy /> Copy Output
              </button>
              <button className="btn btn-warning" onClick={handleExport}>
                <FaFileExport /> Export
              </button>
              <button className="btn btn-info" onClick={handlePreview}>
                Preview in New Tab
              </button>
              <button className="btn btn-info" onClick={handleValidate}>
                Validate
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UniversalConverter;